/**
 * Filterable product grid.
 *
 * Both controls change the grid where it stands by re-requesting the section
 * through the Section Rendering API with the new URL, so Shopify does the
 * filtering and sorting and the count, the grid and the pagination all describe
 * the whole result set rather than the page on screen:
 *
 *   sort_by   the select's value, set on the current URL so applied facets
 *             survive the change.
 *   the pills  the pill's own href, built in Liquid: one facet swapped, the
 *             sort kept, other facets and the page number reset.
 *   the drawer the URL it announces as `facets-drawer:apply` — a category
 *             ticked inside it, which component-facets-drawer applies on the
 *             spot so the size group can unlock beneath it. The drawer stays
 *             open through the render: its groups are refreshed in place
 *             rather than the element replaced, which would close the dialog.
 *
 * Everything is delegated from the document rather than bound inside
 * <product-grid-controls>: the pills sit in the section header, which is
 * outside that element, and the theme editor re-renders this markup on every
 * setting change — a listener bound to a replaced node would go quiet, and
 * customElements.define() is a no-op after the first load so re-registering
 * cannot fix it.
 */

const SORT_PARAM = 'sort_by';

/**
 * Mark the pill that `url` selects, and clear the rest.
 *
 * Liquid decides this from `results.filters`, which Shopify builds out of the
 * result set — so a category that matches no products comes back with no
 * filters at all, nothing reads as applied, and the "All" pill lights up
 * instead of the one that was clicked. The URL still says which category is on,
 * so that is what this reads.
 *
 * It runs after every render and on first load rather than only in the empty
 * case, because "did the server get it right?" is harder to answer than just
 * answering the question again. The rules match the Liquid exactly: a pill is
 * on when the path matches and every value in its own href is applied, and the
 * valueless "All" pill is on when nothing is applied.
 */
function syncPills(wrapper, url) {
  const param = wrapper.dataset.pillParam;
  const applied = param ? url.searchParams.getAll(param) : [];

  for (const pill of wrapper.querySelectorAll('.product-grid__pill')) {
    const pillUrl = new URL(pill.href, window.location.origin);
    /* A tag pill is a path, not a parameter — /collections/<handle>/<tag>. */
    const samePath = pillUrl.pathname === url.pathname;
    const values = param ? pillUrl.searchParams.getAll(param) : [];

    const active = samePath && (values.length ? values.every((value) => applied.includes(value)) : applied.length === 0);

    pill.classList.toggle('product-grid__pill--active', active);
    if (active) {
      pill.setAttribute('aria-current', 'page');
    } else {
      pill.removeAttribute('aria-current');
    }
  }
}

/**
 * Re-request this section with `url`'s parameters and swap its markup in.
 *
 * Resolves true on success. The caller falls back to a real navigation on
 * false, so a failed fetch still filters rather than leaving the control
 * looking broken.
 */
async function renderSection(wrapper, url) {
  const sectionId = wrapper.dataset.section;
  if (!sectionId) return false;

  const request = new URL(url.pathname, window.location.origin);
  request.search = url.search;
  request.searchParams.set('section_id', sectionId);

  wrapper.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(request);
    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = html.getElementById(wrapper.id);
    if (!fresh) throw new Error(`No #${wrapper.id} in the rendered section`);

    wrapper.innerHTML = fresh.innerHTML;
    wrapper.dataset.renderedUrl = `${url.pathname}${url.search}`;
    syncPills(wrapper, url);

    /* The drawer is a sibling of the grid, not inside it, and its ticked values
       and form action come from the same filters — left alone it would still
       show the facets of the previous view. An empty result set has no facets,
       so the section renders no drawer, and the next view may bring one back.

       While the drawer is open it is refreshed, never replaced or removed: a
       replaced <dialog> closes under the shopper, and with no fresh drawer
       the one on screen is what lets them untick whatever emptied the grid. */
    const drawer = wrapper.parentElement?.querySelector(':scope > facets-drawer');
    const freshDrawer = html.querySelector('facets-drawer');
    const drawerOpen = drawer?.querySelector('dialog')?.open ?? false;
    /* A swapped-in drawer connects before `url` is pushed, and it ticks its
       brands from the URL — so it is told which one it was rendered for. */
    if (freshDrawer) freshDrawer.dataset.renderedUrl = `${url.pathname}${url.search}`;
    if (drawer && freshDrawer && drawerOpen) {
      drawer.refresh(freshDrawer, url);
    } else if (drawer && freshDrawer) {
      drawer.replaceWith(freshDrawer);
    } else if (drawer && !drawerOpen) {
      drawer.remove();
    } else if (freshDrawer && !drawer) {
      wrapper.after(freshDrawer);
    }

    wrapper.dispatchEvent(new CustomEvent('product-grid:render', { bubbles: true }));

    return true;
  } catch (error) {
    console.error(error);
    return false;
  } finally {
    wrapper.removeAttribute('aria-busy');
  }
}

/**
 * Render `url` into the grid and record it in history, or navigate to it when
 * the section cannot be fetched.
 */
async function showResults(wrapper, url) {
  if (await renderSection(wrapper, url)) {
    history.pushState({}, '', `${url.pathname}${url.search}`);
  } else {
    window.location.assign(url);
  }
}

if (!customElements.get('product-grid-controls')) {
  customElements.define(
    'product-grid-controls',
    class ProductGridControls extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange);

        /* What the markup on screen was rendered for, so the back button can
           tell whether it needs to fetch. A first load was rendered for the
           address it was loaded at. The path counts as well as the query: a
           tag pill changes only the path. */
        const wrapper = this.closest('.product-grid');
        if (wrapper && wrapper.dataset.renderedUrl === undefined) {
          wrapper.dataset.renderedUrl = `${window.location.pathname}${window.location.search}`;
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      /**
       * Sort in place. The form is a real GET form and submitting it works with
       * JavaScript off; here the same URL is fetched as a section instead, so
       * the shopper keeps their place in the page.
       */
      onChange(event) {
        const select = event.target.closest('select[data-auto-submit]');
        if (!select) return;

        const wrapper = this.closest('.product-grid');
        if (!wrapper) {
          select.form?.requestSubmit();
          return;
        }

        /* Read from the location, not from the form's hidden inputs: the URL
           already carries every facet the shopper has applied, including the
           pill, and it is the thing being pushed. The page number is dropped
           because a new order starts from the top. */
        const url = new URL(window.location.href);
        url.searchParams.set(SORT_PARAM, select.value);
        url.searchParams.delete('page');

        showResults(wrapper, url);
      }
    },
  );
}

document.addEventListener('click', (event) => {
  const pill = event.target.closest('.product-grid__pill');
  if (!pill) return;

  const wrapper = pill.closest('.product-grid');
  if (!wrapper) return;

  event.preventDefault();

  /* Re-selecting the current pill would add a duplicate history entry for no
     change on screen. */
  if (pill.classList.contains('product-grid__pill--active')) return;

  showResults(wrapper, new URL(pill.href, window.location.origin));
});

/* A category ticked in the drawer. The drawer announces the filtered URL and
   leaves rendering to whoever owns the results; with nobody listening it
   submits its form instead. Delegated from the document for the same reason
   the pills are: the drawer element is swapped on every render. The search
   page listens for the same event on its own element, so a drawer with no
   grid beside it is left to that listener. */
document.addEventListener('facets-drawer:apply', (event) => {
  const wrapper = event.target.parentElement?.querySelector(':scope > .product-grid');
  if (!wrapper) return;

  event.preventDefault();
  showResults(wrapper, event.detail.url);
});

/* First load and every theme-editor re-render arrive as server markup that has
   not been through renderSection, so the same correction is applied on the way
   in. A module script runs after the document is parsed, so the grids are here.

   Not inside <product-grid-controls>'s connectedCallback: that element carries
   the sort select and is not rendered at all when sorting is turned off, which
   would leave exactly the grids with no controls unable to fix their pills. */
for (const wrapper of document.querySelectorAll('.product-grid')) {
  syncPills(wrapper, new URL(window.location.href));
}

window.addEventListener('popstate', async () => {
  for (const wrapper of document.querySelectorAll('.product-grid')) {
    /* A fragment-only history entry leaves the path and query alone, and the
       grid on screen already matches it. */
    if (wrapper.dataset.renderedUrl === `${window.location.pathname}${window.location.search}`) continue;

    if (!(await renderSection(wrapper, new URL(window.location.href)))) {
      window.location.reload();
      return;
    }
  }
});
